import React, { useState, useEffect, useRef, useMemo, useLayoutEffect, useCallback } from "react";
import { createPortal } from "react-dom";

import 'mapbox-gl/dist/mapbox-gl.css';
import { ListView } from "../../general_components/content_management/list_view/listView"
// import { LuTimer } from "react-icons/lu";
import styles from "./trailer.module.scss";
import { DateTime } from "luxon";
import { getFormattedTimePassed } from "../../main_board_components/teamCard/truckCard/truckCardComponents/currentState";
import { AddButton } from "../../general_components/content_management/reusableItems/buttons";
import { addNotification, addPopup } from "../../supplementary_components/store";
import { CustomAddForm } from "../../general_components/forms/CustomAddForm";
import { generateShortId } from "../../supplementary_components/supplementaryMethods";
import { API_ROOT, sendRequest } from "../../supplementary_components/requests";
import { flag } from "../../supplementary_components/userDataDelegate";
import { swapSVGWhileProcessing } from "../../general_components/content_management/item_page/driver_page_components/payoutAdjustmentsSection";
import { ConfirmationDialogue } from "../../general_components/forms/ConfirmationDialogue";
import { BsDot } from "react-icons/bs";
import { RiFileCopyLine } from "react-icons/ri";
import { HiArrowNarrowUp } from "react-icons/hi";
import { LuRefreshCcw } from "react-icons/lu";
import { FaTimes } from "react-icons/fa";
import { MdEdit, MdClose } from "react-icons/md";
import MapboxMap, { Marker, Popup } from 'react-map-gl/mapbox-legacy';
import { LoadingIcon } from "../../supplementary_components/loading_icon";
import { Wrapper, Header, Footer, SumbitForm } from "../../general_components/forms/Form";
import styled from "styled-components";

function formatTimeAgo(isoTimestamp, serverTimeISO) {
	if (!isoTimestamp) return '';
	const now = serverTimeISO ? DateTime.fromISO(serverTimeISO) : DateTime.now();
	const result = getFormattedTimePassed(isoTimestamp, now);
	if (typeof result === 'string') return result;
	return result.lbl.replace(/^For /, '') + ' ago';
}

/** Renders above list overflow clipping via fixed positioning + portal. */
function TrailerChipPopupPortal({ anchorRef, children }) {
	const popupRef = useRef(null);
	const [style, setStyle] = useState({ visibility: 'hidden' });

	const updatePosition = useCallback(() => {
		const anchor = anchorRef.current;
		const popup = popupRef.current;
		if (!anchor || !popup) return;

		const anchorRect = anchor.getBoundingClientRect();
		const popupHeight = popup.offsetHeight;
		const popupWidth = popup.offsetWidth;
		const gap = 6;
		const padding = 8;

		let top = anchorRect.bottom + gap;
		if (top + popupHeight > window.innerHeight - padding) {
			const aboveTop = anchorRect.top - popupHeight - gap;
			top = aboveTop >= padding ? aboveTop : Math.max(padding, window.innerHeight - popupHeight - padding);
		}

		let left = anchorRect.left + anchorRect.width / 2;
		const halfWidth = popupWidth / 2;
		left = Math.min(Math.max(left, padding + halfWidth), window.innerWidth - padding - halfWidth);

		setStyle({
			position: 'fixed',
			top,
			left,
			transform: 'translateX(-50%)',
			visibility: 'visible',
		});
	}, [anchorRef]);

	useLayoutEffect(() => {
		updatePosition();
		const raf = requestAnimationFrame(updatePosition);
		window.addEventListener('scroll', updatePosition, true);
		window.addEventListener('resize', updatePosition);
		return () => {
			cancelAnimationFrame(raf);
			window.removeEventListener('scroll', updatePosition, true);
			window.removeEventListener('resize', updatePosition);
		};
	}, [updatePosition]);

	return createPortal(
		<div
			ref={popupRef}
			className={styles.trailerChipPopup}
			data-trailer-chip-popup
			style={style}
		>
			{children}
		</div>,
		document.body
	);
}

function requestTrailerLocationRefresh(trailerNumber) {
	return new Promise((resolve, reject) => {
		if (!trailerNumber) {
			reject();
			return;
		}
		const params = new URLSearchParams({ trailerNumber: String(trailerNumber) });
		sendRequest(
			"POST",
			`${API_ROOT}trailers/refresh-location?${params.toString()}`,
			null,
			() => {
				resolve();
			},
			() => {
				addNotification({ message: "Failed to refresh trailer location", type: "alert" });
				reject();
			},
			null,
			null,
			null,
			true
		);
	});
}

function handleTrailerLocationRefreshClick(e, trailerNumber) {
	e.stopPropagation();
	const btn = e.currentTarget;
	if (btn.querySelector(".spinner")) return;
	const svgTag = btn.querySelector("svg");
	if (!svgTag) return;
	swapSVGWhileProcessing(
		() => requestTrailerLocationRefresh(trailerNumber),
		svgTag,
		null,
		null,
		"0.15rem",
		"var(--light-gray)"
	);
}

function TrailerChipWithPopup({
	trailer,
	timeLbl,
	isOpen,
	onToggle,
	bringToTopMethodRef,
}) {
	const anchorRef = useRef(null);
	const trailerClass = [
		styles.singleTrailer,
		trailer.potentiallyDetached && styles.potentiallyDetached,
		trailer.currentTruck && !trailer.potentiallyDetached && styles.attached,
	].filter(Boolean).join(' ');

	return (
		<div
			ref={anchorRef}
			className={styles.trailerChipWrapper}
			data-trailer-chip-popup
		>
			{isOpen && (
				<TrailerChipPopupPortal anchorRef={anchorRef}>
					{(timeLbl || trailer.manuallyRefreshable) ? (
						<div className={styles.trailerChipPopupRow}>
							<span className={styles.trailerChipPopupLabel}>Last updated:</span>
							{timeLbl ? (
								<span className={styles.trailerChipPopupValue}>{timeLbl}</span>
							) : null}
							{trailer.manuallyRefreshable ? (
								<button
									type="button"
									className={styles.trailerChipPopupRefreshBtn}
									onClick={(e) => handleTrailerLocationRefreshClick(e, trailer.trailerNumber)}
									title="Refresh location"
									aria-label="Refresh location"
								>
									<LuRefreshCcw />
								</button>
							) : null}
						</div>
					) : null}
					{trailer.currentTruck ? (
						<div className={styles.trailerChipPopupRow}>
							<span className={styles.trailerChipPopupLabel}>Truck#:</span>
							<span className={styles.trailerChipPopupValue}>{trailer.currentTruckNumber ?? ''}</span>
						</div>
					) : null}
				</TrailerChipPopupPortal>
			)}
			<div
				className={trailerClass}
				onClick={onToggle}
				style={{ cursor: bringToTopMethodRef ? 'pointer' : undefined }}
			>
				{trailer.trailerNumber}
			</div>
		</div>
	);
}

function copyTrailerCoordinatesToClipboard(trailer) {
	const loc = trailer.lastLocation ?? { lat: trailer.lat, lng: trailer.lng };
	if (loc?.lat == null || loc?.lng == null) return;
	const text = `${loc.lat}, ${loc.lng}`;
	navigator.clipboard.writeText(text).then(() => {
		addNotification({ message: 'Coordinates copied', type: 'success' });
	}).catch(() => {
		addNotification({ message: 'Failed to copy', type: 'alert' });
	});
}

// {listRef, ListConf, Data, Error, NoFooter, RequestReload, PaginationData, filteredData, listBeingUpdated, selectedCategory}
const NewRecordForm = {columns: [
	{
		hiddenField:true,
		name:"id",
		defaultValue:()=>{
			return generateShortId();
		}
	},
	{
		label: "Facility Name",
		name: "name",
		autoComplete: "off",
		mandatory: true,
	},
	{
		label: "Address / Coordinates",
		name: "address",
		autoComplete: "off",
		mandatory: true,
		processBeforeSending: (val) => {
			if (val == null || String(val).trim() === '') return val;
			const str = String(val).trim();
			if (/[a-zA-Z]{2,}/.test(str)) return str;
			const parts = str.split(',').map(s => s.trim()).filter(Boolean);
			if (parts.length === 2 && /^-?\d+\.?\d*$/.test(parts[0]) && /^-?\d+\.?\d*$/.test(parts[1])) {
				return `${parts[0]},${parts[1]}`;
			}
			const dmsRegex = /(\d+)[°]\s*(\d+)[''′]?\s*([\d.]+)[""″]?\s*([NSEW])/g;
			const matches = [...str.matchAll(dmsRegex)];
			if (matches.length >= 2) {
				const toDecimal = (d, m, s, hem) => {
					const dec = Number(d) + Number(m) / 60 + Number(s) / 3600;
					return (hem === 'S' || hem === 'W') ? -dec : dec;
				};
				const latMatch = matches.find(m => m[4] === 'N' || m[4] === 'S');
				const lngMatch = matches.find(m => m[4] === 'E' || m[4] === 'W');
				if (latMatch && lngMatch) {
					return `${toDecimal(latMatch[1], latMatch[2], latMatch[3], latMatch[4]).toFixed(6)},${toDecimal(lngMatch[1], lngMatch[2], lngMatch[3], lngMatch[4]).toFixed(6)}`;
				}
			}
			return val;
		}
	},
	{
		label: "Radius (miles)",
		name: "radius",
		autoComplete: "off",
		mandatory: true,
		defaultValue: () => {
			return ONSITE_RADIUS_MILES;
		},
		filters:[
			(val) => {
				if (val == null || val === '') return val;
				let str = String(val).replace(/[^\d.]/g, '');
				const parts = str.split('.');
				if (parts.length > 2) str = parts[0] + '.' + parts.slice(1).join('');
				if (str.endsWith('.')) return str;
				return parseFloat(str) || 0;
			}
		],
		processBeforeSending: (val) => {
			return parseFloat(val);
		},
	},
	// {
	// 	label: "Latitude and Longitude",
	// 	name: "latlng",
	// 	placeholder: "latitude,longitude",
	// 	autoComplete: "off",
	// 	mandatory: true,
		
	// }
]}
const TrailerPoolListConf = {
	columnProportions: "1.5fr 0.5fr 1fr 60% 3rem",
	dataTitle: "Site",
	dataTitlePlural:"Sites",
	category: 'trailers',
	editAccessLevel: { key: 'TRAILER_MANAGEMENT' },
	columns: [
		{
			label: "Facility Name",
			columnName: "name",
			sortable: true,
			// conditionalFormatting: (value) => {
			// 	return `& > * {}`
			// },
			customComponent: (data) => {
				return <div key={"facilityName-"+data.id} className={styles.poolSiteLabel}>
					<span>{data.name}</span> <RiFileCopyLine onClick={()=>{
						const loc = data.address || data.latlng;
						navigator.clipboard.writeText(loc).then(() => {
							addNotification({ message: 'Copied', type: 'success' });
						}).catch(() => {
							addNotification({ message: 'Failed to copy', type: 'alert' });
						});
					}} />
				</div>
			}
		},
		{
			label: "Radius",
			columnName: "radius",
			// sortable: true,
			valueInterpreter: (value)=>{
                return value + " mi";
            },
			conditionalFormatting: (value) => {
				return `& > * {font-weight:bold; color:var(--gray);}`
			},
		},
		{
			label: "Trailer Count",
			columnName: "trailerCount",
			sortable: true,
			alignment: "center",
			conditionalFormatting: (value) => {
				return `& > * {font-weight:bold; color:var(--gray);}`
			},
		},
		{
			label: "Trailers",
		},
        {
            actionButtons:true,
            notEditableIfValueIs : (val)=>{
                return false;
            },
            onEditAction: (site, postSuccessAction)=>{
				const formData = {
					...NewRecordForm,
					columns: NewRecordForm.columns
						// .filter(column => site[column.name])
						.map(column => ({
							...column,
							defaultValue: () => column.name !== "address" ? site[column.name] : column.name === "address" && site[column.name] ?  site[column.name] :site["latlng"],
						})),
				};
				showTrailerSiteAddForm(formData, "Edit", postSuccessAction);
            },
            onRemoveAction: (site, postSuccessAction)=>{
                addPopup({
                    content:ConfirmationDialogue, 
                    popupData: {
                        confirmationDialogue:true,
                        action:(postResultAction)=>{
                            sendRequest("DELETE", API_ROOT+`trailer-pool-sites/${site.id}`, null, (res)=>{
                                postSuccessAction({ action: "removed", site: site });
                                postResultAction(true);
                                addNotification({message: `Site Was Removed`, type:'success'});
                            }, (e)=>{
                                addNotification({message: e.customError ? e.customError :`Something Went Wrong`, type:'alert'});
                                postResultAction(true);
                            } )
                        },
                        cancelAction:(closePopup)=>{
                            closePopup()
                        },
                        name:"Confirm",
                        formTitle:"Delete Site",
                        submitTitle:"Delete",
                        message:`Are you sure you want to delete "${site.name}"?`
                    }, 
                    postSuccessAction: ()=>{
                        
                    }, 
                    EZCloseAllowed:true
                })
                
               
            }
        }

	]
}
const showTrailerSiteAddForm = (form, buttonLabel, onSiteChange) => {
addPopup({
	content:CustomAddForm, 
	popupData: {
	action:(event, postResultAction)=>{
		event.preventDefault();
		const target = event.target;
		const body = {}
		for(const clmn of form['columns']){
			let inputField = target.querySelector(`input[name="${clmn.name}"]`);
			if(!inputField){
				inputField = target.querySelector(`select[name="${clmn.name}"]`);
			}
			if(inputField && !inputField.disabled){
				body[clmn.name] = clmn.processBeforeSending ? clmn.processBeforeSending(inputField.value) : inputField.value;
			}
		}
		sendRequest('POST', API_ROOT+'trailer-pool-sites',null,(res)=>{
			if (onSiteChange && res?.id) {
				onSiteChange({ action: "edited", site: res });
			}
			postResultAction(true);
		}, ()=>{
			// Error
			postResultAction(false);
			addNotification({message:`Something went wrong`, type:'alert'});
		}, body);
	},
		
	name:buttonLabel,
	formTitle:buttonLabel+" Poo Site",
	submitTitle:buttonLabel,
	method:"POST",
	columns: form['columns']
	}, 
	EZCloseAllowed:true
})
}
const ONSITE_RADIUS_MILES = 0.25; // 400m ≈ 0.248548 miles

function haversineMiles(lat1, lon1, lat2, lon2) {
	const R = 3959; // Earth radius in miles
	const toRad = (d) => d * Math.PI / 180;
	const dLat = toRad(lat2 - lat1);
	const dLon = toRad(lon2 - lon1);
	const a = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) ** 2;
	const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
	return R * c;
}

function parseLatLng(latlngStr) {
	if (!latlngStr || typeof latlngStr !== 'string') return null;
	const parts = latlngStr.split(',').map(s => s.trim());
	if (parts.length !== 2) return null;
	const lat = parseFloat(parts[0]);
	const lng = parseFloat(parts[1]);
	if (Number.isNaN(lat) || Number.isNaN(lng)) return null;
	return { lat, lng };
}

function computeTrailersForSite(site, trailerLocations) {
	const facilityCoords = parseLatLng(site?.latlng);
	const trailers = [];
	if (facilityCoords) {
		for (const [trailerNumber, loc] of Object.entries(trailerLocations ?? {})) {
			if (loc?.lastLocation?.lat == null || loc?.lastLocation?.lng == null) continue;
			const { lat, lng } = loc.lastLocation;
			const distMiles = haversineMiles(facilityCoords.lat, facilityCoords.lng, lat, lng);
			if (distMiles <= site.radius) {
				trailers.push({
					trailerNumber,
					lastUpdated: loc.lastUpdated,
					currentTruck: loc.currentTruck,
					currentTruckNumber: loc.currentTruckNumber,
					potentiallyDetached: loc.potentiallyDetached,
					manuallyRefreshable: loc.manuallyRefreshable,
					lastLocation: loc.lastLocation,
				});
			}
		}
	}
	return { ...site, trailerCount: trailers.length, trailers };
}

export function computeSitesWithTrailers(sites, trailerLocations) {
	return (sites ?? []).map(site => computeTrailersForSite(site, trailerLocations));
}

export function trailersForMap(trailerLocations) {
	return Object.entries(trailerLocations ?? {})
		.filter(([, loc]) => loc?.lastLocation?.lat != null && loc?.lastLocation?.lng != null)
		.map(([trailerNumber, loc]) => ({
			trailerNumber,
			id: trailerNumber,
			lat: loc.lastLocation.lat,
			lng: loc.lastLocation.lng,
			lastUpdated: loc.lastUpdated,
			...loc,
		}));
}

/** Drop ignored trailer numbers from a locations map (map / site chips only). */
export function omitIgnoredTrailerLocations(trailerLocations, ignoredTrailerNumbers) {
	if (!ignoredTrailerNumbers?.length) return { ...(trailerLocations ?? {}) };
	const ignored = new Set(ignoredTrailerNumbers);
	const out = {};
	for (const [k, v] of Object.entries(trailerLocations ?? {})) {
		if (!ignored.has(k)) out[k] = v;
	}
	return out;
}

const IgnoreEditorContent = styled.div`
	margin: 0.1rem 0;
	width: 28rem;
	max-height: 55vh;
	overflow-y: auto;
	padding: 0.5rem 2rem 1rem;
`;

function IgnoredTrailersEditor({ closePopup, popupData }) {
	const [draft, setDraft] = useState(() => [...(popupData?.ignoredTrailerNumbers ?? [])]);
	const [inputValue, setInputValue] = useState("");
	const [loading, setLoading] = useState({ state: false, height: "0" });
	const pageRef = useRef(null);

	useEffect(() => {
		if (pageRef.current) {
			setLoading((prev) => ({ ...prev, height: `${pageRef.current.clientHeight}px` }));
		}
	}, []);

	const addNumber = () => {
		const trimmed = inputValue.trim();
		if (!trimmed) return;
		setDraft((prev) => (prev.includes(trimmed) ? prev : [...prev, trimmed]));
		setInputValue("");
	};

	const removeNumber = (num) => {
		setDraft((prev) => prev.filter((n) => n !== num));
	};

	const save = (event) => {
		event.preventDefault();
		setLoading((prev) => ({ ...prev, state: true }));
		sendRequest(
			"PATCH",
			`${API_ROOT}trailers/ignored-trailer-numbers`,
			null,
			(res) => {
				const ignored = Array.isArray(res?.ignoredTrailerNumbers) ? res.ignoredTrailerNumbers : draft;
				popupData?.onSaved?.(ignored, res?.locationPatches);
				setLoading((prev) => ({ ...prev, state: false }));
				closePopup();
				addNotification({ message: "Ignored trailers updated", type: "success" });
			},
			(e) => {
				setLoading((prev) => ({ ...prev, state: false }));
				addNotification({
					message: e?.customError ? e.customError : "Failed to update ignored trailers",
					type: "alert",
				});
			},
			draft
		);
	};

	return (
		<Wrapper ref={pageRef}>
			<LoadingIcon state={loading.state} height={loading.height} />
			<Header>
				<span>Ignored trailers</span>
			</Header>
			<IgnoreEditorContent className="formContent">
				<SumbitForm method="POST" autoComplete="off" id="ignoredTrailersEditor" onSubmit={save}>
					<p className={styles.ignoreEditorHint}>
						Trailers on this list are hidden from the map and site chips. Enter the number exactly as it appears on the map.
					</p>
					<div className={styles.ignoreEditorAddRow}>
						<input
							type="text"
							value={inputValue}
							placeholder="Trailer number"
							onChange={(e) => setInputValue(e.target.value)}
							onKeyDown={(e) => {
								if (e.key === "Enter") {
									e.preventDefault();
									addNumber();
								}
							}}
						/>
						<button type="button" className={styles.ignoreEditorAddBtn} onClick={addNumber}>
							Add
						</button>
					</div>
					<ul className={styles.ignoreEditorList}>
						{draft.length === 0 ? (
							<li className={styles.ignoreEditorEmpty}>No ignored trailers</li>
						) : (
							draft.map((num) => (
								<li key={num} className={styles.ignoreEditorItem}>
									<span>{num}</span>
									<button
										type="button"
										className={styles.ignoreEditorRemoveBtn}
										onClick={() => removeNumber(num)}
										title="Remove"
										aria-label={`Remove ${num}`}
									>
										<MdClose />
									</button>
								</li>
							))
						)}
					</ul>
				</SumbitForm>
			</IgnoreEditorContent>
			<Footer>
				<div className="non-selectable" onClick={closePopup}>Cancel</div>
				<div>
					<input type="submit" form="ignoredTrailersEditor" value="Save" />
				</div>
			</Footer>
		</Wrapper>
	);
}

function openIgnoredTrailersEditor(ignoredTrailerNumbers, onSaved) {
	addPopup({
		content: IgnoredTrailersEditor,
		popupData: {
			ignoredTrailerNumbers: ignoredTrailerNumbers ?? [],
			onSaved,
		},
		EZCloseAllowed: true,
	});
}

export const TrailerPoolManagement = ({dataset, setDataRef, bringToTopMethodRef}) => {
	const [data, setData] = useState({
		trailerLocations: {},
		datasetSites: [],
		serverTime: null,
		sites: [],
		trailers: [],
		ignoredTrailerNumbers: [],
	});
	const [mapLoaded, setMapLoaded] = useState(false);
	const [mapVisible, setMapVisible] = useState(false);
	const [trailerChipPopup, setTrailerChipPopup] = useState(null);
	const canEditIgnoreList = flag('TRAILER_MANAGEMENT');

	useEffect(() => {
		if (setDataRef) setDataRef.current = setData;
		return () => { if (setDataRef) setDataRef.current = null; };
	}, [setDataRef]);

	useEffect(() => {
		if (!trailerChipPopup) return;
		const onMouseDown = (e) => {
			if (e.target.closest('[data-trailer-chip-popup]')) return;
			setTrailerChipPopup(null);
		};
		document.addEventListener('mousedown', onMouseDown);
		return () => document.removeEventListener('mousedown', onMouseDown);
	}, [trailerChipPopup]);

	useEffect(() => {
		const ignoredTrailerNumbers = Array.isArray(dataset?.ignoredTrailerNumbers)
			? dataset.ignoredTrailerNumbers
			: [];
		const trailerLocations = omitIgnoredTrailerLocations(
			dataset?.trailerLocations ?? {},
			ignoredTrailerNumbers
		);
		const serverTime = dataset?.currentTime;
		const datasetSites = dataset?.sites ?? [];
		setData(prev => {
			const sites = (computeSitesWithTrailers(datasetSites, trailerLocations) ?? []).map(s => ({ ...s, _serverTime: serverTime }));
			return {
				trailerLocations,
				datasetSites,
				serverTime,
				sites,
				trailers: trailersForMap(trailerLocations),
				ignoredTrailerNumbers,
			};
		});
	}, [dataset?.sites, dataset?.trailerLocations, dataset?.currentTime, dataset?.ignoredTrailerNumbers]);

	const onIgnoredTrailersSaved = useCallback((ignored, locationPatches) => {
		setData(prev => {
			const merged = { ...(prev.trailerLocations ?? {}) };
			if (locationPatches && typeof locationPatches === 'object') {
				for (const [k, v] of Object.entries(locationPatches)) {
					if (v == null) delete merged[k];
					else merged[k] = v;
				}
			}
			const filtered = omitIgnoredTrailerLocations(merged, ignored);
			const sites = (computeSitesWithTrailers(prev.datasetSites ?? [], filtered) ?? []).map(s => ({
				...s,
				_serverTime: prev.serverTime,
			}));
			return {
				...prev,
				ignoredTrailerNumbers: ignored,
				trailerLocations: filtered,
				sites,
				trailers: trailersForMap(filtered),
			};
		});
	}, []);

	const onSiteChange = (response) => {
		if (!response?.action) return;
		const { action, site } = response;
		setData(prev => {
			const trailerLocations = prev.trailerLocations ?? dataset?.trailerLocations ?? {};
			if (action === "removed") {
				const datasetSites = (prev.datasetSites ?? []).filter(s => s.id !== site?.id);
				const sites = (computeSitesWithTrailers(datasetSites, trailerLocations) ?? []).map(s => ({ ...s, _serverTime: prev.serverTime }));
				return { ...prev, datasetSites, sites };
			}
			if (action === "edited" && site?.id) {
				const siteWithTrailers = computeTrailersForSite(site, trailerLocations);
				const existingIndex = prev.sites.findIndex(s => s.id === site.id);
				const editedSite = { ...siteWithTrailers, _serverTime: dataset?.currentTime ?? prev.sites[existingIndex]?._serverTime };
				const newSites = existingIndex >= 0
					? [...prev.sites.slice(0, existingIndex), editedSite, ...prev.sites.slice(existingIndex + 1)]
					: [...prev.sites, editedSite];
				return { ...prev, sites: newSites };
			}
			return prev;
		});
	};
	const listConf = useMemo(() => ({
		...TrailerPoolListConf,
		columns: TrailerPoolListConf.columns.map((col) =>
			col.label === "Trailers"
				? {
						...col,
						customComponent: (rowData) => (
							<div key={"trailer-" + rowData.name} className={styles.trailersContainer}>
								{rowData.trailers.map((trailer) => {
									const timeLbl = trailer.lastUpdated ? formatTimeAgo(trailer.lastUpdated, rowData._serverTime) : '';
									const popupKey = `${rowData.id ?? rowData.name}-${trailer.trailerNumber}`;
									const isPopupOpen = trailerChipPopup?.key === popupKey;
									const handleChipClick = () => {
										bringToTopMethodRef?.current?.(trailer.trailerNumber);
										setTrailerChipPopup(isPopupOpen ? null : { key: popupKey });
									};
									return (
										<TrailerChipWithPopup
											key={"trailer-" + trailer.trailerNumber}
											trailer={trailer}
											timeLbl={timeLbl}
											isOpen={isPopupOpen}
											onToggle={handleChipClick}
											bringToTopMethodRef={bringToTopMethodRef}
										/>
									);
								})}
							</div>
						),
					}
				: col
		),
	}), [bringToTopMethodRef, trailerChipPopup]);

	const handleShowMap = () => {
		if (!mapLoaded) setMapLoaded(true);
		setMapVisible(true);
	};
	const handleHideMap = () => setMapVisible(false);

	return <>
		<div className={styles.mapToggleRow}>
			{!mapLoaded ? (
				<button type="button" className={styles.mapToggleBtn} onClick={handleShowMap}>
					Show map
				</button>
			) : (
				<button type="button" className={styles.mapToggleBtn} onClick={mapVisible ? handleHideMap : handleShowMap}>
					{mapVisible ? 'Hide map' : 'Show map'}
				</button>
			)}
		</div>
		{mapLoaded && (
			<div className={styles.mapContainer} style={{ display: mapVisible ? undefined : 'none' }}>
				<TrailerMap
					trailers={data.trailers}
					serverTime={data.serverTime}
					bringToTopMethodRef={bringToTopMethodRef}
					canEditIgnoreList={canEditIgnoreList}
					onEditIgnoreList={() => openIgnoredTrailersEditor(data.ignoredTrailerNumbers, onIgnoredTrailersSaved)}
				/>
			</div>
		)}
		<br />
		{data.sites?.length > 0 ? <ListView ListConf={listConf} Data={data.sites} Error={null} NoFooter={true} RequestReload={onSiteChange} PaginationData={null} filteredData={null} listBeingUpdated={false} selectedCategory={null} /> : null}
			<br />
		<AddButton customSize={"1.6rem"} action={()=>{
			showTrailerSiteAddForm(NewRecordForm, "Add", onSiteChange);
		}} />
		<br />
		
	</>
}

const MAP_STYLES = {
	streets: 'mapbox://styles/mapbox/light-v11',
	satellite: 'mapbox://styles/mapbox/satellite-streets-v12'
	
};

// Continental US (lower 48) bounds [[west, south], [east, north]] – excludes AK, HI
const US_BOUNDS = [[-125, 24], [-66, 50]];

// Zoom -> cluster radius (km). Cell size in degrees ≈ radius/111.
const ZOOM_CLUSTER_KM = { 0: 1500, 1: 1200, 2: 500, 3: 250, 4: 90, 5: 25, 6: 10, 7: 3, 8: 0.1, 9: 0.01, 10: 0.001 };
const KM_PER_DEGREE = 111;

/** Marker uses anchor="bottom", so lat/lng sits on the pin's bottom edge. Lift popup so its tip targets the pin top. */
const TRAILER_MAP_PIN_HEIGHT_PX = 22;
const TRAILER_MAP_POPUP_OFFSET = [0, -(TRAILER_MAP_PIN_HEIGHT_PX)];

function clusterTrailers(trailers, zoom) {
	const zoomKey = Math.floor(zoom);
	const radiusKm = ZOOM_CLUSTER_KM[zoomKey] ?? (zoomKey < 3 ? 500 : 0.001);
	const cellSize = radiusKm / KM_PER_DEGREE;
	const key = (t) => `${Math.floor(t.lat / cellSize)},${Math.floor(t.lng / cellSize)}`;
	const groups = new Map();
	for (const t of trailers) {
		const k = key(t);
		if (!groups.has(k)) groups.set(k, []);
		groups.get(k).push(t);
	}
	return Array.from(groups.values()).map(group => ({
		trailers: group,
		lat: group[0].lat,
		lng: group[0].lng
	}));
}

function trailerMapKey(trailer) {
	return String(trailer?.trailerNumber ?? trailer?.id ?? '');
}

function sameTrailerKeySets(a, b) {
	if (!a?.length || !b?.length || a.length !== b.length) return false;
	const sortedA = [...a].sort();
	const sortedB = [...b].sort();
	return sortedA.every((key, i) => key === sortedB[i]);
}

const TrailerMap = ({ trailers, serverTime, bringToTopMethodRef, canEditIgnoreList, onEditIgnoreList }) => {
	const [mapStyle, setMapStyle] = useState(MAP_STYLES.streets);
	const [filterPattern, setFilterPattern] = useState('');
	const [selectedTrailerKeys, setSelectedTrailerKeys] = useState(null);
	const [zoom, setZoom] = useState(3.5);
	const mapRef = useRef();

	const filteredTrailers = useMemo(() => {
		if (!trailers?.length) return [];
		if (filterPattern.trim() === '') return trailers;
		const pattern = filterPattern.trim().toLowerCase();
		return trailers.filter(t => {
			const key = String(t.trailerNumber ?? t.id ?? '').toLowerCase();
			return key.includes(pattern);
		});
	}, [trailers, filterPattern]);

	const liveSelectedCluster = useMemo(() => {
		if (!selectedTrailerKeys?.length) return null;
		const liveTrailers = selectedTrailerKeys
			.map((key) => filteredTrailers.find((t) => trailerMapKey(t) === key))
			.filter(Boolean);
		if (!liveTrailers.length) return null;
		return {
			trailers: liveTrailers,
			lat: liveTrailers[0].lat,
			lng: liveTrailers[0].lng,
		};
	}, [selectedTrailerKeys, filteredTrailers]);

	useEffect(() => {
		if (selectedTrailerKeys?.length && !liveSelectedCluster) {
			setSelectedTrailerKeys(null);
		}
	}, [liveSelectedCluster, selectedTrailerKeys]);

	const handleMapClick = useCallback(() => {
		setSelectedTrailerKeys(null);
	}, []);

	const handleMarkerClick = useCallback((cluster) => (e) => {
		e.originalEvent?.stopPropagation?.();
		const keys = cluster.trailers.map(trailerMapKey);
		setSelectedTrailerKeys((prev) => (sameTrailerKeySets(prev, keys) ? null : keys));
	}, []);

	if (!trailers?.length && !canEditIgnoreList) return null;

	const clusters = clusterTrailers(filteredTrailers, zoom);
	// console.log("process.env.REACT_APP_MAPBOX_TOKEN")
	return (
		<div style={{ width: '100%' }}>
			
			<div style={{ position: 'relative', height: '40rem', width: '100%' }}>
			<MapboxMap
				ref={mapRef}
				initialViewState={{
					longitude: -95.5,
					latitude: 39.5,
					zoom: 3.5,
					pitch: 0,
					bearing: 0
				}}
				onMoveEnd={(e) => setZoom(e.viewState.zoom)}
				onClick={handleMapClick}
				onLoad={() => {
					mapRef.current?.fitBounds(US_BOUNDS, { padding: 24 });
				}}
				projection="mercator"
				maxPitch={0}
				style={{ width: '100%', height: '100%' }}
				mapStyle={mapStyle}
				mapboxAccessToken={process.env.REACT_APP_MAPBOX_TOKEN}
			>
				{clusters.map((cluster, idx) => (
					<Marker
						key={`${cluster.lat}-${cluster.lng}-${idx}`}
						longitude={cluster.lng}
						latitude={cluster.lat}
						anchor="bottom"
						onClick={handleMarkerClick(cluster)}
					>
						<div className={cluster.trailers.length > 1 ? styles.customPinCluster : `${styles.customPin} ${cluster.trailers[0].potentiallyDetached ? styles.potentiallyDetached : cluster.trailers[0].currentTruck ? styles.attached : ''}`}>
							{cluster.trailers.length > 1 ? cluster.trailers.length : (cluster.trailers[0].trailerNumber ?? cluster.trailers[0].id)}
						</div>
					</Marker>
				))}
				{liveSelectedCluster && (
					<Popup
						key={`${liveSelectedCluster.lat}-${liveSelectedCluster.lng}-${trailerMapKey(liveSelectedCluster.trailers[0])}`}
						longitude={liveSelectedCluster.lng}
						latitude={liveSelectedCluster.lat}
						anchor="bottom"
						offset={TRAILER_MAP_POPUP_OFFSET}
						closeButton
						closeOnClick
						onClose={() => setSelectedTrailerKeys(null)}
						className={styles.trailerMapPopup}
					>
						<div className={styles.trailerPopup} >
							{liveSelectedCluster.trailers.length === 1 ? (
								<>
									<TrailerPopup trailer={liveSelectedCluster.trailers[0]} serverTime={serverTime} bringToTopMethodRef={bringToTopMethodRef} />
								</>
							) : (
								<>
									<div className={styles.trailerPopupTitle}>{liveSelectedCluster.trailers.length} Trailers</div>
									<div className={styles.trailerPopupMeta}>
										<div className={styles.trailerPopupListSection}>
											<span className={styles.trailerPopupLabel}>Trailers:</span>
											<div className={styles.trailerPopupList}>
												{liveSelectedCluster.trailers.map((t) => (
													<span key={t.id ?? t.trailerNumber} className={styles.trailerPopupListItem}>
														<BsDot /> {t.trailerNumber ?? t.id}
														{bringToTopMethodRef && (
															<button type="button" onClick={() => bringToTopMethodRef?.current?.(t.trailerNumber ?? t.id)} title="Bring to top" aria-label="Bring to top">
																<HiArrowNarrowUp />
															</button>
														)}
														{((t.lastLocation?.lat != null) || (t.lat != null && t.lng != null)) && (
															<button type="button" onClick={() => copyTrailerCoordinatesToClipboard(t)} title="Copy coordinates" aria-label="Copy coordinates">
																<RiFileCopyLine />
															</button>
														)}
													</span>
												))}
											</div>
										</div>
									</div>
								</>
							)}
						</div>
					</Popup>
				)}
				<div className={styles.mapTopRightControls}>
					<div className={styles.mapStyleToggleRow}>
						<div className={styles.mapStyleToggle}>
							<button
								type="button"
								onClick={() => setMapStyle(MAP_STYLES.streets)}
								className={`${styles.mapStyleToggleButton} ${mapStyle === MAP_STYLES.streets ? styles.active : styles.inactive}`}
							>
								Streets
							</button>
							<button
								type="button"
								onClick={() => setMapStyle(MAP_STYLES.satellite)}
								className={`${styles.mapStyleToggleButton} ${mapStyle === MAP_STYLES.satellite ? styles.active : styles.inactive}`}
							>
								Satellite
							</button>
						</div>
						{canEditIgnoreList ? (
							<button
								type="button"
								className={styles.mapIgnoreEditBtn}
								onClick={(e) => {
									e.stopPropagation();
									onEditIgnoreList?.();
								}}
								title="Edit ignored trailers"
								aria-label="Edit ignored trailers"
							>
								<MdEdit />
							</button>
						) : null}
					</div>
					<div className={styles.mapFilterInput}>
						<div className={styles.mapFilterInputInner}>
							<input
								type="text"
								placeholder="Filter trailers..."
								value={filterPattern}
								onChange={e => setFilterPattern(e.target.value)}
							/>
							{filterPattern ? (
								<button
									type="button"
									className={styles.mapFilterClearBtn}
									onClick={(e) => {
										e.stopPropagation();
										setFilterPattern('');
									}}
									title="Clear filter"
									aria-label="Clear filter"
								>
									<FaTimes />
								</button>
							) : null}
						</div>
					</div>
				</div>
			</MapboxMap>
			</div>
		</div>
	);
};

const TrailerPopup = ({ trailer, serverTime, bringToTopMethodRef }) => {
	const timeLbl = formatTimeAgo(trailer.lastUpdated, serverTime);
	const locationStr = trailer.lastLocation?.address?.city && trailer.lastLocation?.address?.stateOrProvince
		? `${trailer.lastLocation.address.city}, ${trailer.lastLocation.address.stateOrProvince}`
		: null;
	return (
		<>
			<div className={styles.trailerPopupTitle}>
				{trailer.trailerNumber ?? trailer.id}
				{((trailer.lastLocation?.lat != null) || (trailer.lat != null && trailer.lng != null)) && (
					<button type="button" className={styles.trailerPopupCopyBtn} onClick={() => copyTrailerCoordinatesToClipboard(trailer)} title="Copy coordinates" aria-label="Copy coordinates">
						<RiFileCopyLine />
					</button>
				)}
				{bringToTopMethodRef && (
					<button type="button" className={styles.trailerPopupCopyBtn} onClick={() => bringToTopMethodRef?.current?.(trailer.trailerNumber ?? trailer.id)} title="Bring to top" aria-label="Bring to top">
						<HiArrowNarrowUp />
					</button>
				)}
				{trailer.manuallyRefreshable ? (
					<button
						type="button"
						className={styles.trailerPopupCopyBtn}
						onClick={(e) => handleTrailerLocationRefreshClick(e, trailer.trailerNumber ?? trailer.id)}
						title="Refresh location"
						aria-label="Refresh location"
					>
						<LuRefreshCcw />
					</button>
				) : null}
			</div>
			<div className={styles.trailerPopupMeta}>
				{timeLbl ? (
					<div className={styles.trailerPopupRow}>
						<span className={styles.trailerPopupLabel}>Last updated:</span>
						<span className={styles.trailerPopupValue}>{timeLbl}</span>
					</div>
				) : null}
				{trailer.currentTruck ? (
					<div className={styles.trailerPopupRow}>
						<span className={styles.trailerPopupLabel}>Truck#:</span>
						<span className={styles.trailerPopupValue}>{trailer.currentTruckNumber ?? ''}</span>
					</div>
				) : null}
				{locationStr ? (
					<div className={styles.trailerPopupRow}>
						<span className={styles.trailerPopupLabel}>Location:</span>
						<span className={styles.trailerPopupValue}>{locationStr}</span>
					</div>
				) : null}
			</div>
		</>
	);
};


